import React, { useMemo, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import hljs from 'highlight.js';
import 'highlight.js/styles/vs2015.css';
import { Box, Typography, FormHelperText, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';

// Настройка highlight.js для подсветки кода
if (typeof window !== 'undefined') {
    window.hljs = hljs;
}

const StyledEditorWrapper = styled(Box)(({ theme, error }) => ({
    '& .quill': {
        border: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        '&:hover': {
            borderColor: error ? theme.palette.error.main : theme.palette.text.primary,
        },
    },
    '& .ql-container': {
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        fontSize: '14px',
        fontFamily: theme.typography.fontFamily,
    },
    '& .ql-toolbar': {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .ql-editor': {
        fontSize: '14px',
        minHeight: 'inherit',
        '&.ql-blank::before': {
            color: theme.palette.text.secondary,
            fontStyle: 'normal',
        },
    },
    '& .ql-editor p': {
        marginBottom: theme.spacing(1),
    },
    '& .ql-editor pre': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    '& .ql-editor code': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
        padding: '2px 4px',
        borderRadius: '3px',
        fontSize: '13px',
        fontFamily: 'monospace',
    },
    '& .ql-syntax': {
        backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#2d2d2d',
        color: '#d4d4d4',
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: '13px',
    },
}));

// Preset конфигурации для разных случаев использования
const TOOLBAR_PRESETS = {
    full: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['clean']
    ],
    basic: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['code-block'],
        ['clean']
    ],
    minimal: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'bullet' }],
        ['code-block'],
        ['clean']
    ],
};

const RichTextEditor = ({
    value,
    onChange,
    placeholder = 'Start typing...',
    label,
    error,
    helperText,
    readOnly = false,
    minHeight = '200px',
    maxHeight,
    toolbar = 'full', // 'full', 'basic', 'minimal', false, or custom array
    enableCodeHighlight = true,
    enableAutoSave = false,
    autoSaveKey = null,
    autoSaveInterval = 30000, // 30 seconds
    onAutoSave,
    showLastSaved = false,
}) => {
    const [lastSaved, setLastSaved] = React.useState(null);

    // Modules configuration
    const modules = useMemo(() => {
        const config = {
            toolbar: typeof toolbar === 'string' ? TOOLBAR_PRESETS[toolbar] : toolbar,
        };

        // Add syntax highlighting if enabled
        if (enableCodeHighlight) {
            config.syntax = {
                highlight: (text) => hljs.highlightAuto(text).value,
            };
        }

        return config;
    }, [toolbar, enableCodeHighlight]);

    // Formats
    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet', 'indent',
        'blockquote', 'code-block',
        'link', 'image',
        'color', 'background',
        'align',
    ];

    // Auto-save functionality
    const saveDraft = useCallback(() => {
        if (autoSaveKey && value) {
            try {
                localStorage.setItem(autoSaveKey, value);
                setLastSaved(new Date());
                if (onAutoSave) {
                    onAutoSave(value);
                }
            } catch (error) {
                console.error('Failed to save draft:', error);
            }
        }
    }, [value, autoSaveKey, onAutoSave]);

    // Load draft on mount
    useEffect(() => {
        if (enableAutoSave && autoSaveKey && !value) {
            try {
                const saved = localStorage.getItem(autoSaveKey);
                if (saved && onChange) {
                    onChange(saved);
                    setLastSaved(new Date());
                }
            } catch (error) {
                console.error('Failed to load draft:', error);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSaveKey, enableAutoSave]);

    // Auto-save interval
    useEffect(() => {
        if (enableAutoSave && autoSaveKey) {
            const timer = setInterval(saveDraft, autoSaveInterval);
            return () => clearInterval(timer);
        }
    }, [enableAutoSave, autoSaveKey, autoSaveInterval, saveDraft]);

    // Calculate editor height
    const editorStyle = useMemo(() => {
        const style = {};
        if (minHeight) {
            style.minHeight = minHeight;
        }
        if (maxHeight) {
            style.maxHeight = maxHeight;
            style.overflow = 'auto';
        }
        return style;
    }, [minHeight, maxHeight]);

    return (
        <Box>
            {(label || (showLastSaved && lastSaved)) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    {label && (
                        <Typography
                            variant="subtitle2"
                            sx={{ color: error ? 'error.main' : 'text.primary' }}
                        >
                            {label}
                        </Typography>
                    )}
                    {showLastSaved && lastSaved && (
                        <Chip
                            label={`Saved: ${lastSaved.toLocaleTimeString()}`}
                            size="small"
                            variant="outlined"
                            color="success"
                        />
                    )}
                </Box>
            )}
            <StyledEditorWrapper error={error}>
                <ReactQuill
                    theme="snow"
                    value={value}
                    onChange={onChange}
                    modules={modules}
                    formats={formats}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    style={editorStyle}
                />
            </StyledEditorWrapper>
            {helperText && (
                <FormHelperText error={error} sx={{ mt: 0.5, mx: 1.75 }}>
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
};

export default RichTextEditor;
